import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  FieldGroup,
  FormField,
  Heading,
  Input,
  Modal,
  MultiSelect,
  NoticeBanner,
  NoticeModal,
  Select,
  Stack,
  Text,
  Textarea,
  type Notice,
  type SelectOption,
} from '@hkdevs/design-system';
import { useId, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import type { AdminNotice, NoticeCreate } from '../../api/queries/notices';
import { useEnabledRoles } from '../../api/queries/roles';
import { offersRole } from '../../auth/roles';

/**
 * The New notice modal and its Edit mode (pc-dbb.18): title, message, audience, tone,
 * the days it shows, acknowledgement and blocking, with a live preview of the banner (and
 * of the blocking dialog). The API's field errors show on the fields they name.
 */

type NoticeTone = AdminNotice['tone'];
type Role = NonNullable<AdminNotice['targetRoles']>[number];

export interface NoticeFormValues {
  title: string;
  body: string;
  tone: NoticeTone;
  /** DatePicker's local-midnight dates. */
  startsOn: Date | null;
  endsOn: Date | null;
  requiresAck: boolean;
  blocking: boolean;
  /** The roles it shows to, or null for all users. */
  targetRoles: Role[] | null;
}

/** The roles in the order they're listed, each as a group of people. */
export const ROLE_PLURALS: [Role, string][] = [
  ['rep', 'Reps'],
  ['rep_intern', 'Rep interns'],
  ['supervisor', 'Supervisors'],
  ['pay_viewer', 'Pay viewers'],
  ['admin', 'Admins'],
];

/** Who it shows to: "All users", or its roles in order, "Reps, Supervisors". */
export function audienceLabel(targetRoles: readonly Role[] | null) {
  if (targetRoles === null) return 'All users';
  return ROLE_PLURALS.filter(([role]) => targetRoles.includes(role))
    .map(([, plural]) => plural)
    .join(', ');
}

const WEB_ADDRESS = /(https?:\/\/[^\s]+[^\s.,;:!?)])/;

/** The body's text, with its web addresses as links. */
export function linkify(text: string): ReactNode {
  return text.split(WEB_ADDRESS).map((part, index) =>
    index % 2 ? (
      <a key={index} href={part} target="_blank" rel="noreferrer">
        {part}
      </a>
    ) : (
      part
    ),
  );
}

/** A calendar date ("2026-10-02") as a DatePicker's local-midnight Date. */
export const toDate = (date: string) => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y!, m! - 1, d);
};

/** A DatePicker's local-midnight Date as a calendar date. */
export const isoDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** A new notice: info, from today, until acknowledged. */
export const emptyNotice = (today: Date): NoticeFormValues => ({
  title: '',
  body: '',
  tone: 'info',
  startsOn: today,
  endsOn: null,
  requiresAck: true,
  blocking: false,
  targetRoles: null,
});

/** An existing notice as the form's values. */
export const noticeValues = (notice: AdminNotice): NoticeFormValues => ({
  title: notice.title,
  body: notice.body,
  tone: notice.tone,
  startsOn: toDate(notice.startsOn),
  endsOn: notice.endsOn === null ? null : toDate(notice.endsOn),
  requiresAck: notice.requiresAck,
  blocking: notice.blocking,
  targetRoles: notice.targetRoles && [...notice.targetRoles],
});

const TONE_OPTIONS: SelectOption[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'danger', label: 'Urgent' },
];

const AUDIENCE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All users' },
  { value: 'roles', label: 'Specific roles' },
];

const ROLE_OPTIONS = ROLE_PLURALS.map(([value, label]) => ({ value, label }));

export interface NoticeFormErrors {
  title?: string;
  targetRoles?: string;
  startsOn?: string;
  endsOn?: string;
  blocking?: string;
  /** Not about one field: the API's detail. */
  form?: string;
}

/** What's wrong with the values, in words that explain the fix. */
export function validateNotice(values: NoticeFormValues): NoticeFormErrors {
  const errors: NoticeFormErrors = {};
  if (!values.title.trim()) errors.title = 'Enter a title.';
  if (values.targetRoles?.length === 0)
    errors.targetRoles = 'Choose at least one role, or show it to all users.';
  if (!values.startsOn) errors.startsOn = 'Choose the first day it shows.';
  if (!values.endsOn && !values.requiresAck)
    errors.endsOn = 'Choose the last day it shows, or make users acknowledge it.';
  else if (values.endsOn && values.startsOn && values.endsOn < values.startsOn)
    errors.endsOn = 'Choose a last day on or after the first day.';
  return errors;
}

/** The request body: trimmed, blocking only with acknowledgement, roles in order. */
export function toNoticeBody(values: NoticeFormValues): Required<NoticeCreate> {
  return {
    title: values.title.trim(),
    body: values.body.trim(),
    tone: values.tone,
    startsOn: values.startsOn ? isoDay(values.startsOn) : null,
    endsOn: values.endsOn ? isoDay(values.endsOn) : null,
    requiresAck: values.requiresAck,
    blocking: values.requiresAck && values.blocking,
    targetRoles:
      values.targetRoles &&
      ROLE_PLURALS.map(([role]) => role).filter((role) => values.targetRoles!.includes(role)),
  };
}

const FIELDS = new Set(['title', 'targetRoles', 'startsOn', 'endsOn', 'blocking']);

/**
 * The API's error as form errors: each field error under its field (the location may
 * start with `body`), anything else as the form's error. Undefined for errors that aren't
 * about the input (a network failure, a 5xx), which the caller shows as a toast.
 */
export function noticeFormErrors(error: unknown): NoticeFormErrors | undefined {
  if (!(error instanceof ApiError) || error.status >= 500) return undefined;
  const errors: NoticeFormErrors = {};
  for (const fieldError of error.problem.errors ?? []) {
    const loc = fieldError.loc[0] === 'body' ? fieldError.loc.slice(1) : fieldError.loc;
    const field = String(loc[0]) as keyof NoticeFormErrors;
    if (FIELDS.has(field)) errors[field] ??= fieldError.message;
    else errors.form ??= fieldError.message;
  }
  if (Object.keys(errors).length === 0) {
    errors.form = error.problem.detail ?? 'The notice couldn’t be saved.';
  }
  return errors;
}

/** Who sees it, for the preview: "All users see it.", "Reps and Supervisors see it." */
export function whoSees(targetRoles: readonly Role[] | null) {
  if (targetRoles === null) return 'All users see it.';
  if (targetRoles.length === 0) return 'Nobody sees it until you choose a role.';
  const names = audienceLabel(targetRoles).split(', ');
  const last = names.pop()!;
  return `${names.length ? `${names.join(', ')} and ${last}` : last} see it.`;
}

/** The form's values as the notice a user would see. */
const previewOf = (values: NoticeFormValues): Notice => ({
  id: 'preview',
  tone: values.tone,
  title: values.title.trim() || 'Untitled notice',
  ...(values.body.trim() && { body: linkify(values.body.trim()) }),
  requiresAck: values.requiresAck,
});

export interface NoticeEditorProps {
  onClose: () => void;
  mode: 'create' | 'edit';
  /** The form's values when it opens: emptyNotice(today) to create, the notice's to edit. */
  initialValues: NoticeFormValues;
  /** Today, for the date pickers. */
  today: Date;
  /** Called with valid values. */
  onSubmit: (values: NoticeFormValues) => void;
  /** The save is in flight: the fields and buttons wait. */
  submitting?: boolean;
  /** The API's errors for the last save. */
  serverErrors?: NoticeFormErrors | undefined;
}

/** Post a notice, or edit one. Remount it (a `key`) to start again from initialValues. */
export function NoticeEditor({
  onClose,
  mode,
  initialValues,
  today,
  onSubmit,
  submitting = false,
  serverErrors,
}: NoticeEditorProps) {
  const previewId = useId();
  const [values, setValues] = useState(initialValues);
  const [showErrors, setShowErrors] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const errors: NoticeFormErrors = {
    ...serverErrors,
    ...(showErrors ? validateNotice(values) : {}),
  };
  const set = <K extends keyof NoticeFormValues>(key: K, value: NoticeFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const editing = mode === 'edit';
  const preview = previewOf(values);
  const { data: enabledRoles } = useEnabledRoles();
  // Switched-off roles aren't offered, unless the notice already targets one (ADR 0028).
  const roleOptions = ROLE_OPTIONS.filter((option) =>
    offersRole(enabledRoles, option.value, initialValues.targetRoles ?? []),
  );

  const submit = () => {
    setShowErrors(true);
    if (Object.keys(validateNotice(values)).length) return;
    onSubmit(values);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={editing ? 'Edit notice' : 'New notice'}
      description="It shows at the top of every page, from its first day."
      dismissable={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            loading={submitting}
            loadingText={editing ? 'Saving…' : 'Posting…'}
          >
            {editing ? 'Save' : 'Post notice'}
          </Button>
        </>
      }
    >
      <FieldGroup>
        {errors.form && (
          <Alert tone="danger" title="The notice couldn’t be saved">
            {errors.form}
          </Alert>
        )}
        <FormField label="Title" required error={errors.title}>
          <Input
            value={values.title}
            autoComplete="off"
            disabled={submitting}
            onChange={(event) => set('title', event.target.value)}
          />
        </FormField>
        <FormField label="Message" hint="A sentence or two. Web addresses become links.">
          <Textarea
            value={values.body}
            rows={3}
            autoResize
            disabled={submitting}
            onChange={(event) => set('body', event.target.value)}
          />
        </FormField>
        <FieldGroup layout="row">
          <FormField label="Audience">
            <Select
              options={AUDIENCE_OPTIONS}
              value={values.targetRoles === null ? 'all' : 'roles'}
              disabled={submitting}
              onChange={(event) => set('targetRoles', event.target.value === 'all' ? null : [])}
            />
          </FormField>
          {values.targetRoles !== null && (
            <FormField label="Roles" required error={errors.targetRoles}>
              <MultiSelect
                options={roleOptions}
                value={values.targetRoles}
                placeholder="Choose roles"
                disabled={submitting}
                onChange={(roles) => set('targetRoles', roles as Role[])}
              />
            </FormField>
          )}
        </FieldGroup>
        <FormField label="Tone">
          <Select
            options={TONE_OPTIONS}
            value={values.tone}
            disabled={submitting}
            onChange={(event) => set('tone', event.target.value as NoticeTone)}
          />
        </FormField>
        <FieldGroup layout="row">
          <FormField label="First day" required error={errors.startsOn}>
            <DatePicker
              allowTextInput
              value={values.startsOn}
              today={today}
              disabled={submitting}
              onChange={(date) => set('startsOn', date)}
            />
          </FormField>
          <FormField
            label="Last day"
            required={!values.requiresAck}
            hint={
              values.requiresAck
                ? 'Leave empty to show it until everyone acknowledges it.'
                : undefined
            }
            error={errors.endsOn}
          >
            <DatePicker
              value={values.endsOn}
              today={today}
              min={values.startsOn}
              clearable
              allowTextInput
              disabled={submitting}
              onChange={(date) => set('endsOn', date)}
            />
          </FormField>
        </FieldGroup>
        <Stack gap={2}>
          <Checkbox
            label="Users must acknowledge it"
            checked={values.requiresAck}
            disabled={submitting}
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                requiresAck: event.target.checked,
                blocking: event.target.checked && current.blocking,
              }))
            }
          />
          <Checkbox
            label="Block the app until they acknowledge it"
            checked={values.blocking}
            disabled={submitting || !values.requiresAck}
            onChange={(event) => set('blocking', event.target.checked)}
          />
          {errors.blocking && (
            <Text as="p" tone="danger" size="meta">
              {errors.blocking}
            </Text>
          )}
        </Stack>
        {values.blocking && (
          <Alert tone="warning" title="This interrupts everyone">
            Nobody can use the app until they acknowledge it. Keep it for what everyone must read,
            such as a change to how they’re paid.
          </Alert>
        )}
        <Stack as="section" aria-labelledby={previewId} gap={2}>
          <Heading level={3} size="label" id={previewId}>
            Preview
          </Heading>
          <Text as="p" tone="muted">
            {whoSees(values.targetRoles)}
          </Text>
          <NoticeBanner
            aria-label="Banner preview"
            notices={[preview]}
            onAcknowledge={() => {}}
            onDismiss={() => {}}
          />
          {values.blocking && (
            <div>
              <Button size="sm" variant="secondary" onClick={() => setPreviewing(true)}>
                Preview the blocking dialog
              </Button>
            </div>
          )}
        </Stack>
      </FieldGroup>
      <NoticeModal
        notice={previewing && values.blocking ? preview : null}
        onAcknowledge={() => setPreviewing(false)}
        acknowledgeLabel="I acknowledge (close preview)"
      />
    </Modal>
  );
}
